import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import type { LoyaltyCurrency } from '../partnership';
import { getLoyaltyCurrencies } from '../partnership/api';
import { initialLoyaltyCurrenciesContextValue } from './LoyaltyCurrenciesContext.constants';
import type { LoyaltyCurrenciesContextValue } from './LoyaltyCurrenciesContext.types';
import { AuthContext } from '../auth';
import { mapTagToLoyaltyCurrency } from '../../lib/listingUtils';

export const LoyaltyCurrenciesContext = createContext<LoyaltyCurrenciesContextValue>(initialLoyaltyCurrenciesContextValue);

export const LoyaltyCurrenciesProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { data: loyaltyCurrencies, isLoading: areLoyaltyCurrenciesLoading, error } = useQuery(
    'getLoyaltyCurrencies',
    () => getLoyaltyCurrencies(),
  );

  const { accountTags, isSignedIn, updateAccountCardDetails } = useContext(AuthContext);

  const [selectedLoyaltyCurrency, setSelectedLoyaltyCurrency] = useState<LoyaltyCurrency>(initialLoyaltyCurrenciesContextValue.selectedLoyaltyCurrency);

  useEffect(() => {
    if (!!loyaltyCurrencies && !error) {
      if (isSignedIn && accountTags && accountTags.accountLoyaltyUnitTags.length > 0) {
        setSelectedLoyaltyCurrency(mapTagToLoyaltyCurrency(accountTags.accountLoyaltyUnitTags[0], loyaltyCurrencies) ?? initialLoyaltyCurrenciesContextValue.selectedLoyaltyCurrency);
      } else {
        const firstAvailableLoyaltyCurrency: LoyaltyCurrency | undefined = Object.values(loyaltyCurrencies)?.[0];
        setSelectedLoyaltyCurrency(firstAvailableLoyaltyCurrency ?? initialLoyaltyCurrenciesContextValue.selectedLoyaltyCurrency);
      }
    }
  }, [loyaltyCurrencies, error, accountTags, isSignedIn]);

  // Update account card details when loyalty currencies change
  useEffect(() => {
    updateAccountCardDetails(loyaltyCurrencies);
  }, [loyaltyCurrencies, updateAccountCardDetails]);

  return (
    <LoyaltyCurrenciesContext.Provider value={{
      selectedLoyaltyCurrency,
      setSelectedLoyaltyCurrency,
      loyaltyCurrencies,
      areLoyaltyCurrenciesLoading,
      error: error as Error | undefined,
    }}>
      {children}
    </LoyaltyCurrenciesContext.Provider>
  );
};
